import { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react'

import { useTree } from './use-tree'
import { TreeNode, Map, Device } from './types'
import { useFloorNavigation } from './use-floor-navigation'
import useActions, { type UseDevice } from './use-actions'

type AccessPointZonesContextType = {
  tree: TreeNode[]
  loading: boolean
  mapUuid: string | null
  initialOpenState: Record<string, boolean>
  devicesOnMap: Device[]
  deviceHovered: Device | null
  onMapUuidChange: (uuid: string) => void
  onFloorPrev: () => void
  onFloorNext: () => void
  apiActions: UseDevice
  onDeviceOver: (device:Device|null) => void
}

const AccessPointZonesContext = createContext<AccessPointZonesContextType | null>(null)

type AccessPointZonesProviderProps = {
  children: ReactNode
}

const findFirstMapWithDevices = (tree: TreeNode[]): string | null => {
  const items = tree[0].data.type === 'site'
    ? tree[0].children
    : tree
  for (const surfaceArea of items) {
    for (const map of surfaceArea.children) {
      if (map.data.type === 'map' && (map.data as Map).devices?.length > 0) {
        return map.id
      }
    }
  }
  return null
}

const findParentSurfaceArea = (tree: TreeNode[], mapUuid: string | null): string | null => {
  if (!mapUuid) {
    return null
  }

  for (const surfaceArea of tree) {
    for (const map of surfaceArea.children) {
      if (map.id === mapUuid) {
        return surfaceArea.id
      }
    }
  }
  return null
}

export const AccessPointZonesProvider = ({ children }: AccessPointZonesProviderProps) => {
  const { tree, loading } = useTree()
  const [deviceHovered, setDeviceHovered] = useState<Device | null>(null)
  const [mapUuid, setMapUuid] = useState<string | null>(null)

  const { onFloorPrev, onFloorNext } = useFloorNavigation({
    tree,
    mapUuid,
    onMapUuidChange: (uuid) => setMapUuid(uuid),
  })
  const apiActions = useActions()

  const initialOpenState = useMemo(() => {
    if (!tree.length) {
      return {}
    }
    const items = tree[0].data.type === 'site'
      ? tree[0].children
      : tree
    const parentId = findParentSurfaceArea(items, mapUuid)
    if (!parentId) {
      return {}
    }

    return {
      [parentId]: true,
      [mapUuid as string]: true,
      ...(tree[0].data.type === 'site' && {[tree[0].id]: true})
    }
  }, [tree, mapUuid])

  const devicesOnMap = useMemo(() => {
    if (!tree.length) {
      return []
    }
    const items = tree[0].data.type === 'site'
      ? tree[0].children
      : tree
    const surface = items.find(({ children }) => children.some(({ id }) => id === mapUuid))
    if (!surface) {
      return []
    }
    const map = surface.children.find(({ id }) => id === mapUuid)
    if (!map) {
      return []
    }
    return map.children
      .filter(({ data }) => data.type !== 'empty')
      .map(({ data }) => data as Device)
  }, [mapUuid, tree])

  const handleMapUuidChange = useCallback((uuid: string) => {
    setMapUuid(uuid)
  }, [])

  useEffect(() => {
    if (tree.length === 0 || mapUuid) {
      return
    }

    const firstMapUuid = findFirstMapWithDevices(tree)
    if (firstMapUuid) {
      setMapUuid(firstMapUuid)
    }
  }, [tree, mapUuid])

  return (
    <AccessPointZonesContext.Provider value={{
      tree,
      loading,
      mapUuid,
      initialOpenState,
      devicesOnMap,
      deviceHovered,
      onMapUuidChange: handleMapUuidChange,
      apiActions,
      onFloorPrev,
      onFloorNext,
      onDeviceOver: setDeviceHovered
    }}>
      {children}
    </AccessPointZonesContext.Provider>
  )
}

export const useAccessPointZonesContext = (): AccessPointZonesContextType => {
  const context = useContext(AccessPointZonesContext)

  if (!context) {
    throw new Error('useAccessPointZonesContext must be used within an AccessPointZonesProvider')
  }

  return context
}

export default AccessPointZonesContext
